import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  Pagination,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RedeemIcon from '@mui/icons-material/Redeem';
import FilterListIcon from '@mui/icons-material/FilterList';
import { createCDKey, redeemCDKey, getCDKeys, deleteCDKeys } from '../services/api';
import { AuthContext } from '../context/AuthContext';

const CDKeyManagement = () => {
  const [duration, setDuration] = useState('');
  const [batchCount, setBatchCount] = useState(1);
  const [cdkey, setCDKey] = useState('');
  const [createdCDKeys, setCreatedCDKeys] = useState([]);
  const [redeemMessage, setRedeemMessage] = useState('');
  const [cdkeys, setCDKeys] = useState([]);
  const [filteredCDKeys, setFilteredCDKeys] = useState([]);
  const [filter, setFilter] = useState({
    code: '',
    duration: '',
    usedBy: '',
    usedByValue: '',
    createdAt: ''
  });
  const [selectedCDKeys, setSelectedCDKeys] = useState([]);
  const { user } = useContext(AuthContext);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const isAdmin = user && user.user_group === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchCDKeys();
    }
  }, [isAdmin]);

  useEffect(() => {
    applyFilters();
  }, [cdkeys, filter, page, rowsPerPage]);

  const fetchCDKeys = async () => {
    try {
      const response = await getCDKeys();
      setCDKeys(response.data);
    } catch (error) {
      console.error('Error fetching CDKeys:', error);
    }
  };

  const applyFilters = () => {
    let filtered = cdkeys || [];

    if (filter.code) {
      filtered = filtered.filter(cdkey => cdkey.code.toLowerCase().includes(filter.code.toLowerCase()));
    }
    if (filter.duration) {
      filtered = filtered.filter(cdkey => cdkey.duration === parseInt(filter.duration));
    }
    if (filter.usedBy === 'unused') {
      filtered = filtered.filter(cdkey => !cdkey.used_by);
    } else if (filter.usedBy === 'used') {
      if (filter.usedByValue) {
        filtered = filtered.filter(cdkey => cdkey.used_by === parseInt(filter.usedByValue));
      } else {
        filtered = filtered.filter(cdkey => cdkey.used_by);
      }
    }
    if (filter.createdAt) {
      filtered = filtered.filter(cdkey => new Date(cdkey.created_at).toLocaleDateString() === new Date(filter.createdAt).toLocaleDateString());
    }

    const totalFilteredCount = filtered.length;
    const effectiveRowsPerPage = rowsPerPage === 'all' ? totalFilteredCount : parseInt(rowsPerPage);
    
    setTotalPages(Math.ceil(totalFilteredCount / effectiveRowsPerPage));

    const start = (page - 1) * effectiveRowsPerPage;
    const end = Math.min(start + effectiveRowsPerPage, totalFilteredCount);
    setFilteredCDKeys(filtered.slice(start, end));
  };

  const handleCreateCDKeys = async (e) => {
    e.preventDefault();
    try {
      const response = await createCDKey({ duration: parseInt(duration), count: parseInt(batchCount) });
      setCreatedCDKeys(response.data.map(cdkey => cdkey.code));
      fetchCDKeys();
    } catch (error) {
      console.error('Error creating CDKeys:', error);
    }
  };

  const handleRedeemCDKey = async (e) => {
    e.preventDefault();
    try {
      const response = await redeemCDKey({ cdkey });
      setRedeemMessage(`CDKey redeemed successfully. VIP expires at: ${response.data.vip_expires_at}`);
    } catch (error) {
      setRedeemMessage('Error redeeming CDKey. Please try again.');
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await deleteCDKeys(selectedCDKeys);
      setSelectedCDKeys([]);
      fetchCDKeys();
    } catch (error) {
      console.error('Error deleting CDKeys:', error);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedCDKeys(filteredCDKeys.map(cdkey => cdkey.id));
    } else {
      setSelectedCDKeys([]);
    }
  };

  const handleSelectCDKey = (id) => {
    setSelectedCDKeys(prev =>
      prev.includes(id) ? prev.filter(key => key !== id) : [...prev, id]
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = event.target.value;
    setRowsPerPage(value);
    setPage(1);
  };

  const exportToCSV = () => {
  const csvRows = [];
  const headers = ['CDKey', 'Duration', 'Created At', 'Used By'];
  csvRows.push(headers.join(','));

  filteredCDKeys.forEach(cdkey => {
    const row = [
      cdkey.code,
      cdkey.duration,
      new Date(cdkey.created_at).toLocaleString(),
      cdkey.used_by ? cdkey.used_by : 'Not used'
    ];
    csvRows.push(row.join(','));
  });

  const csvString = csvRows.join('\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', 'cdkeys.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

return (
  <Container maxWidth="lg">
    <Box sx={{ my: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        CDKey Management Dashboard
      </Typography>
      <Grid container spacing={3}>
        {isAdmin && (
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Create CDKeys
                </Typography>
                <Box component="form" onSubmit={handleCreateCDKeys}>
                  <TextField
                    required
                    fullWidth
                    label="Duration (days)"
                    type="number"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    required
                    fullWidth
                    label="Batch Count"
                    type="number"
                    value={batchCount}
                    onChange={(e) => setBatchCount(e.target.value)}
                    inputProps={{ min: 1, max: 100 }}
                    sx={{ mb: 2 }}
                  />
                  <Button type="submit" variant="contained" startIcon={<AddIcon />}>
                    Create CDKeys
                  </Button>
                </Box>
                {createdCDKeys.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Created CDKeys:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {createdCDKeys.map((key, index) => (
                        <Chip key={index} label={key} color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12} md={isAdmin ? 6 : 12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Redeem CDKey
              </Typography>
              <Box component="form" onSubmit={handleRedeemCDKey}>
                <TextField
                  required
                  fullWidth
                  label="CDKey"
                  value={cdkey}
                  onChange={(e) => setCDKey(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Button type="submit" variant="contained" startIcon={<RedeemIcon />}>
                  Redeem CDKey
                </Button>
              </Box>
              {redeemMessage && (
                <Box sx={{ mt: 2 }}>
                  <Typography color={redeemMessage.includes('successfully') ? 'success.main' : 'error.main'}>
                    {redeemMessage}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        {isAdmin && (
          <Grid item xs={12}>
            <Card elevation={3}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    All CDKeys ({cdkeys?.length || 0})
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                  </Button>
                </Box>
                {showFilters && (
                  <Box sx={{ mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          label="Filter by Code"
                          value={filter.code}
                          onChange={(e) => setFilter({ ...filter, code: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          label="Filter by Duration"
                          type="number"
                          value={filter.duration}
                          onChange={(e) => setFilter({ ...filter, duration: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Select
                          fullWidth
                          label="Filter by Used By"
                          value={filter.usedBy}
                          onChange={(e) => setFilter({ ...filter, usedBy: e.target.value, usedByValue: '' })}
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="unused">Unused</MenuItem>
                          <MenuItem value="used">Used</MenuItem>
                        </Select>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          label="Filter by Created At"
                          type="date"
                          value={filter.createdAt}
                          onChange={(e) => setFilter({ ...filter, createdAt: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteSelected}
                    disabled={selectedCDKeys.length === 0}
                  >
                    Delete Selected ({selectedCDKeys.length})
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={exportToCSV}
                    sx={{ ml: 2 }}
                  >
                    Export to CSV
                  </Button>
                </Box>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            onChange={handleSelectAll}
                            checked={selectedCDKeys.length === filteredCDKeys.length && filteredCDKeys.length > 0}
                            indeterminate={selectedCDKeys.length > 0 && selectedCDKeys.length < filteredCDKeys.length}
                          />
                        </TableCell>
                        <TableCell>CDKey</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Used By</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCDKeys.map((cdkey) => (
                        <TableRow key={cdkey.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedCDKeys.includes(cdkey.id)}
                              onChange={() => handleSelectCDKey(cdkey.id)}
                            />
                          </TableCell>
                          <TableCell>{cdkey.code}</TableCell>
                          <TableCell>{cdkey.duration} days</TableCell>
                          <TableCell>{new Date(cdkey.created_at).toLocaleString()}</TableCell>
                          <TableCell>
                            {cdkey.used_by ? (
                              <Chip label={cdkey.used_by} color="primary" size="small" />
                            ) : (
                              <Chip label="Not used" variant="outlined" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Delete">
                              <IconButton onClick={() => handleSelectCDKey(cdkey.id)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                    Showing {Math.min((page - 1) * (rowsPerPage === 'all' ? filteredCDKeys.length : parseInt(rowsPerPage)) + 1, filteredCDKeys.length)} to {Math.min(page * (rowsPerPage === 'all' ? filteredCDKeys.length : parseInt(rowsPerPage)), filteredCDKeys.length)} of {cdkeys?.length || 0} entries
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handleChangePage}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                    <Select
                        value={rowsPerPage}
                        onChange={handleChangeRowsPerPage}
                        variant="outlined"
                        sx={{ ml: 2 }}
                    >
                        <MenuItem value={10}>10 per page</MenuItem>
                        <MenuItem value={25}>25 per page</MenuItem>
                        <MenuItem value={50}>50 per page</MenuItem>
                        <MenuItem value="all">All</MenuItem>
                    </Select>
                    </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  </Container>
);
};

export default CDKeyManagement;
